import React from 'react';
import { Ticket } from '../types';

interface PrintableTicketProps {
  ticket: Ticket;
  qrCodeData: string;
}

/**
 * Print-only ticket layout.
 *
 * Hidden on screen (display: none) and only revealed inside an `@media print`
 * context, so it never affects the on-screen receipt. Sized for thermal
 * receipt paper (80mm) but degrades gracefully on standard A4/Letter.
 *
 * Triggered from ReceiptPage via `window.print()`.
 */
const PrintableTicket: React.FC<PrintableTicketProps> = ({ ticket, qrCodeData }) => {
  const formattedPrice = `€${parseFloat(String(ticket.price || 0)).toFixed(2)}`;
  const issuedAt = ticket.created_at ? new Date(ticket.created_at).toLocaleString() : 'N/A';

  return (
    <div className="printable-ticket">
      <div className="pt-inner">
        {/* Header */}
        <div className="pt-header">
          <div className="pt-title">ADMISSION TICKET</div>
          <div className="pt-subtitle">Keep this receipt for entry</div>
        </div>

        <div className="pt-divider" />

        {/* QR Code */}
        <div className="pt-qr-wrap">
          {qrCodeData ? (
            <div className="pt-qr" dangerouslySetInnerHTML={{ __html: qrCodeData }} />
          ) : (
            <div className="pt-qr-placeholder">QR code not available</div>
          )}
        </div>

        {/* Ticket number */}
        <div className="pt-number-block">
          <div className="pt-label">Ticket ID</div>
          <div className="pt-number">{ticket.number}</div>
        </div>

        <div className="pt-divider" />

        {/* Details */}
        <div className="pt-row">
          <span className="pt-label">Type</span>
          <span className="pt-value pt-capitalize">{ticket.type}</span>
        </div>
        <div className="pt-row">
          <span className="pt-label">Price</span>
          <span className="pt-value pt-price">{formattedPrice}</span>
        </div>
        {ticket.status && (
          <div className="pt-row">
            <span className="pt-label">Status</span>
            <span className="pt-value pt-uppercase">{ticket.status}</span>
          </div>
        )}
        <div className="pt-row">
          <span className="pt-label">Issued</span>
          <span className="pt-value">{issuedAt}</span>
        </div>

        <div className="pt-divider" />

        {/* Footer */}
        <div className="pt-footer">
          <div>Valid for one-time use only</div>
          <div className="pt-txn">TXN: {ticket.transaction_id}</div>
        </div>
      </div>

      <style>{`
        .printable-ticket {
          display: none;
        }

        @media print {
          /* Reveal the printable layout and reset the page for the printer. */
          .printable-ticket {
            display: block !important;
            color: #000;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          * {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          .pt-inner {
            width: 100%;
            max-width: 80mm;
            margin: 0 auto;
            padding: 4mm 3mm;
            box-sizing: border-box;
            text-align: center;
          }

          .pt-header { margin-bottom: 3mm; }
          .pt-title {
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 1px;
          }
          .pt-subtitle {
            font-size: 9px;
            color: #444;
            margin-top: 1mm;
          }

          .pt-divider {
            border-top: 1px dashed #000;
            margin: 3mm 0;
          }

          .pt-qr-wrap {
            margin: 2mm 0;
          }
          .pt-qr {
            width: 45mm;
            height: 45mm;
            margin: 0 auto;
          }
          .pt-qr svg {
            width: 100%;
            height: 100%;
            display: block;
          }
          .pt-qr-placeholder {
            width: 45mm;
            height: 45mm;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #ccc;
            font-size: 9px;
            color: #888;
          }

          .pt-number-block { margin: 2mm 0; }
          .pt-number {
            font-family: monospace;
            font-size: 11px;
            font-weight: 600;
            word-break: break-all;
            padding: 0 2mm;
          }

          .pt-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 11px;
            padding: 1mm 0;
          }
          .pt-label {
            font-size: 9px;
            color: #444;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .pt-value { font-weight: 600; }
          .pt-price { font-size: 14px; font-weight: 700; }
          .pt-capitalize { text-transform: capitalize; }
          .pt-uppercase { text-transform: uppercase; }

          .pt-footer {
            font-size: 9px;
            color: #555;
            margin-top: 2mm;
            line-height: 1.5;
          }
          .pt-txn { word-break: break-all; }

          @page {
            size: 80mm auto;
            margin: 5mm;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintableTicket;
